import { cn, VisuallyHidden } from '@hbs-us/ui';
import { MoreHorizontal } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

/**
 * Shared `_ops` block — row-action menu (UI-SPEC §4 "Inline row actions").
 *
 * A keyboard-operable dropdown for per-row actions (View / Edit / Archive). The
 * icon-only `MoreHorizontal` trigger carries a `VisuallyHidden` label +
 * `aria-label` (A11Y-01: no unlabelled icon-only control). Built with NO new
 * dependency — a self-contained accessible menu (`role="menu"` / `role="menuitem"`)
 * with arrow-key navigation, Home/End, Esc-to-close-and-restore-focus, and
 * click-outside dismissal. The 44px trigger meets the target floor; every color
 * is a locked token (no hex, BRAND-06). Destructive items render the destructive
 * token; the CALLER routes them through `_money/ConfirmDialog` before mutating.
 *
 * Composition primitive: it renders the passed items and fires their `onSelect`,
 * it does not fetch.
 */

export interface MoreMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  /** Destructive items render in the destructive token. */
  destructive?: boolean;
  disabled?: boolean;
}

export function MoreMenu({
  label,
  items,
  align = 'end',
}: {
  /** Accessible label for the icon-only trigger (a t() string in app code). */
  label: string;
  items: MoreMenuItem[];
  /** Horizontal alignment of the menu relative to the trigger. */
  align?: 'start' | 'end';
}) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const enabledIndexes = items.map((item, i) => (item.disabled ? -1 : i)).filter((i) => i >= 0);

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const openMenu = useCallback(() => {
    setActiveIndex(enabledIndexes[0] ?? 0);
    setOpen(true);
  }, [enabledIndexes]);

  // Move keyboard focus to the active menu item while open.
  useEffect(() => {
    if (open) itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  // Dismiss on outside click.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const moveActive = useCallback(
    (direction: 1 | -1) => {
      if (enabledIndexes.length === 0) return;
      const position = enabledIndexes.indexOf(activeIndex);
      const nextPosition = (position + direction + enabledIndexes.length) % enabledIndexes.length;
      setActiveIndex(enabledIndexes[nextPosition]);
    },
    [activeIndex, enabledIndexes],
  );

  const handleMenuKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveActive(-1);
          break;
        case 'Home':
          event.preventDefault();
          setActiveIndex(enabledIndexes[0] ?? 0);
          break;
        case 'End':
          event.preventDefault();
          setActiveIndex(enabledIndexes[enabledIndexes.length - 1] ?? 0);
          break;
        case 'Escape':
          event.preventDefault();
          close();
          break;
        case 'Tab':
          close(false);
          break;
        default:
          break;
      }
    },
    [moveActive, enabledIndexes, close],
  );

  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openMenu();
      }
    },
    [openMenu],
  );

  return (
    <div className="relative inline-block text-left">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={handleTriggerKeyDown}
        className="inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <MoreHorizontal className="size-4" aria-hidden="true" />
        <VisuallyHidden>{label}</VisuallyHidden>
      </button>
      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={handleMenuKeyDown}
          className={cn(
            'absolute z-50 mt-1 min-w-40 rounded-md border border-border bg-card p-1 shadow-lg',
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          {items.map((item, index) => (
            <button
              key={item.key}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              type="button"
              role="menuitem"
              tabIndex={index === activeIndex ? 0 : -1}
              disabled={item.disabled}
              onClick={() => {
                item.onSelect();
                close();
              }}
              className={cn(
                'flex h-11 w-full items-center rounded-md px-3 text-left text-sm transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
                item.destructive ? 'text-destructive' : 'text-foreground',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
